<template>
  <div class="seller-dashboard pa-3 pa-sm-5 pa-md-8">
    <!-- Top Bar -->
    <div class="d-flex justify-space-between align-center mb-6 mb-md-10">
      <div class="hidden-sm-and-down" style="width: 100%; max-width: 400px;">
        <v-text-field placeholder="Search here" solo flat hide-details background-color="white"
          class="modern-search rounded-lg">
          <template v-slot:append>
            <v-icon color="#9CA3AF">mdi-magnify</v-icon>
          </template>
        </v-text-field>
      </div>

      <div class="d-flex align-center gap-3 ml-auto">
        <!-- Notification Bell -->
        <v-menu v-model="notifMenu" offset-y left :close-on-content-click="false" max-width="360" min-width="320">
          <template #activator="{ on, attrs }">
            <v-btn icon color="#2D9CDB" class="modern-icon-btn blue lighten-5" v-bind="attrs" v-on="on">
              <v-badge :content="unreadCount > 99 ? '99+' : String(unreadCount)" :value="unreadCount > 0"
                color="#FF5B5B" overlap>
                <v-icon>mdi-bell-outline</v-icon>
              </v-badge>
            </v-btn>
          </template>

          <v-card class="notif-card" elevation="4">
            <div class="d-flex justify-space-between align-center px-4 pt-4 pb-2">
              <span class="font-weight-bold text-body-1" style="color:#111827">การแจ้งเตือน</span>
              <v-btn v-if="unreadCount > 0" text x-small color="#2D9CDB" @click="markAllRead">
                อ่านทั้งหมด
              </v-btn>
            </div>
            <v-divider />

            <div v-if="notifLoading" class="pa-4 text-center">
              <v-progress-circular indeterminate color="#2D9CDB" size="24" />
            </div>

            <v-list v-else-if="notifications.length" class="pa-0 notif-list">
              <v-list-item v-for="n in notifications" :key="n.id"
                :class="['notif-item', { 'notif-unread': !n.isRead }]"
                @click="handleNotifClick(n)">
                <v-list-item-avatar size="36" :color="notifColor(n.type)" class="mr-3">
                  <v-icon small color="white">{{ notifIcon(n.type) }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-bold" style="color:#1f2937">
                    {{ n.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption mt-1" style="white-space:normal;line-height:1.4">
                    {{ n.message }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption mt-1" style="color:#9CA3AF">
                    {{ timeAgo(n.createdAt) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="!n.isRead">
                  <div class="notif-dot"></div>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <div v-else class="pa-6 text-center">
              <v-icon size="40" color="grey lighten-2">mdi-bell-off-outline</v-icon>
              <p class="text-caption grey--text mt-2 mb-0">ยังไม่มีการแจ้งเตือน</p>
            </div>
          </v-card>
        </v-menu>

        <!-- Chat -->
        <v-menu v-model="chatMenu" offset-y left :close-on-content-click="false" max-width="360" min-width="320">
          <template #activator="{ on, attrs }">
            <v-btn icon color="#2D9CDB" class="modern-icon-btn blue lighten-5" v-bind="attrs" v-on="on">
              <v-badge :content="chatUnreadCount > 99 ? '99+' : String(chatUnreadCount)"
                :value="chatUnreadCount > 0" color="#FF5B5B" overlap>
                <v-icon>mdi-message-text-outline</v-icon>
              </v-badge>
            </v-btn>
          </template>

          <v-card class="notif-card" elevation="4">
            <div class="d-flex justify-space-between align-center px-4 pt-4 pb-2">
              <span class="font-weight-bold text-body-1" style="color:#111827">ข้อความ</span>
              <v-btn text x-small color="#2D9CDB" @click="goToChat">ดูทั้งหมด</v-btn>
            </div>
            <v-divider />

            <div v-if="chatLoading" class="pa-4 text-center">
              <v-progress-circular indeterminate color="#2D9CDB" size="24" />
            </div>

            <v-list v-else-if="chatConversations.length" class="pa-0 notif-list">
              <v-list-item v-for="c in chatConversations" :key="c.id"
                :class="['notif-item', { 'notif-unread': c.unreadCount > 0 }]"
                @click="goToChatConversation(c.id)">
                <v-list-item-avatar size="36" color="#E3F2FD" class="mr-3">
                  <img v-if="resolveChatAvatar(c.user)" :src="resolveChatAvatar(c.user)" />
                  <v-icon v-else color="#2D9CDB" small>mdi-account</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-bold" style="color:#1f2937">
                    {{ c.user && (c.user.firstName || c.user.username) || 'ผู้ใช้' }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption mt-1" style="white-space:normal;line-height:1.4">
                    {{ c.lastMessage ? c.lastMessage.content : 'ยังไม่มีข้อความ' }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption mt-1" style="color:#9CA3AF">
                    {{ c.lastMessage ? timeAgo(c.lastMessage.createdAt) : '' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="c.unreadCount > 0">
                  <v-chip x-small color="#FF5B5B" text-color="white" class="font-weight-bold px-2">
                    {{ c.unreadCount > 99 ? '99+' : c.unreadCount }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <div v-else class="pa-6 text-center">
              <v-icon size="40" color="grey lighten-2">mdi-message-off-outline</v-icon>
              <p class="text-caption grey--text mt-2 mb-0">ยังไม่มีข้อความ</p>
            </div>
          </v-card>
        </v-menu>

        <div class="d-flex align-center ml-4 pl-4" style="border-left: 1px solid #E0E6ED;">
          <div class="text-right mr-3 hidden-sm-and-down">
            <div class="text-body-2 font-weight-bold" style="color: #2D3748;">สวัสดี, {{ sellerDisplayName }}</div>
          </div>
          <v-avatar size="44" class="cursor-pointer">
            <img v-if="sellerAvatar" :src="sellerAvatar" alt="User" @error="avatarError = true" />
            <v-icon v-else size="40" color="#F4811F">mdi-account-circle</v-icon>
          </v-avatar>
        </div>
      </div>
    </div>

    <!-- Title Row -->
    <div class="d-flex justify-space-between align-end mb-5 mb-md-8 flex-wrap" style="gap:12px">
      <div>
        <h1 class="dashboard-title">Dashboard</h1>
        <p class="dashboard-subtitle mt-1">ยินดีต้อนรับ, {{ sellerDisplayName }}!</p>
      </div>
      <div>
        <v-menu offset-y transition="slide-y-transition" rounded="lg">
          <template v-slot:activator="{ on, attrs }">
            <v-card class="filter-card d-flex align-center px-4 py-3 cursor-pointer" v-bind="attrs" v-on="on"
              elevation="0">
              <div class="filter-icon-box mr-3">
                <v-icon color="#2D9CDB" small>mdi-calendar-blank</v-icon>
              </div>
              <div>
                <div class="text-caption grey--text text--lighten-1"
                  style="font-size: 10px !important; line-height: 1;">Filter ช่วงเวลา</div>
                <div class="text-caption font-weight-bold" style="font-size: 12px !important;">
                  {{ periodDisplayDate }}
                </div>
              </div>
              <v-icon small color="grey lighten-1" class="ml-4">mdi-chevron-down</v-icon>
            </v-card>
          </template>
          <v-list dense>
            <v-list-item link v-for="opt in periodOptions" :key="opt.value"
              @click="selectPeriod(opt.value)"
              :class="{ 'primary--text font-weight-bold': selectedPeriod === opt.value }">
              <v-list-item-title>{{ opt.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Metrics Row -->
    <v-row>
      <v-col cols="12" sm="6" md="3" v-for="metric in metrics" :key="metric.title">
        <v-skeleton-loader v-if="loadingStats" type="card" class="rounded-xl" height="110" />
        <MetricCard v-else
          :title="metric.title"
          :value="metric.value"
          :subtitle="metric.subtitle"
          :icon="metric.icon"
          :badge-icon="metric.badgeIcon"
          :trendUp="metric.trendUp" />
      </v-col>
    </v-row>

    <!-- Charts Row 1 -->
    <v-row class="mt-4">
      <!-- Order Status Pie Chart -->
      <v-col cols="12" md="6">
        <v-card class="dashboard-card pa-6 h-100" elevation="0">
          <div class="d-flex justify-space-between align-center mb-8">
            <h3 class="card-title">สถานะออเดอร์</h3>
            <v-chip small outlined color="grey">ตลอดทั้งหมด</v-chip>
          </div>

          <div v-if="loadingStats" class="d-flex justify-space-around align-end px-4">
            <v-skeleton-loader v-for="i in 3" :key="i" type="avatar" width="130" height="130" class="mx-2" />
          </div>
          <div v-else class="d-flex justify-space-around align-end px-1 px-sm-4 flex-wrap" style="gap:16px">
            <div class="text-center d-flex flex-column align-center">
              <div class="chart-circle-wrapper mb-3" :style="{ width: pieSize + 'px', height: pieSize + 'px' }">
                <v-progress-circular rotate="-90" :size="pieSize" :width="pieStroke"
                  :value="orderPieData.deliveredPct" color="#00C48C" class="custom-progress">
                  <span class="font-weight-black" :class="pieFontClass" style="color:#2D3748">{{ orderPieData.deliveredPct }}%</span>
                </v-progress-circular>
                <div class="circle-bg"></div>
              </div>
              <div class="text-caption font-weight-bold grey--text text--darken-1">จัดส่งสำเร็จ</div>
              <div class="text-caption grey--text">{{ orderPieData.delivered }} ออเดอร์</div>
            </div>

            <div class="text-center d-flex flex-column align-center">
              <div class="chart-circle-wrapper mb-3" :style="{ width: pieSize + 'px', height: pieSize + 'px' }">
                <v-progress-circular rotate="-90" :size="pieSize" :width="pieStroke"
                  :value="orderPieData.inProgressPct" color="#2D9CDB" class="custom-progress">
                  <span class="font-weight-black" :class="pieFontClass" style="color:#2D3748">{{ orderPieData.inProgressPct }}%</span>
                </v-progress-circular>
                <div class="circle-bg"></div>
              </div>
              <div class="text-caption font-weight-bold grey--text text--darken-1">กำลังดำเนินการ</div>
              <div class="text-caption grey--text">{{ orderPieData.inProgress }} ออเดอร์</div>
            </div>

            <div class="text-center d-flex flex-column align-center">
              <div class="chart-circle-wrapper mb-3" :style="{ width: pieSize + 'px', height: pieSize + 'px' }">
                <v-progress-circular rotate="-90" :size="pieSize" :width="pieStroke"
                  :value="orderPieData.cancelledPct" color="#FF5B5B" class="custom-progress">
                  <span class="font-weight-black" :class="pieFontClass" style="color:#2D3748">{{ orderPieData.cancelledPct }}%</span>
                </v-progress-circular>
                <div class="circle-bg"></div>
              </div>
              <div class="text-caption font-weight-bold grey--text text--darken-1">ยกเลิก/คืนเงิน</div>
              <div class="text-caption grey--text">{{ orderPieData.cancelled }} ออเดอร์</div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Chart Order (Wave) -->
      <v-col cols="12" md="6">
        <v-card class="dashboard-card pa-6 h-100" elevation="0">
          <div class="d-flex justify-space-between mb-8">
            <div>
              <h3 class="card-title">จำนวนออเดอร์รายวัน</h3>
              <p class="text-caption grey--text mt-1">7 วันล่าสุด</p>
            </div>
          </div>

          <div v-if="loadingStats">
            <v-skeleton-loader type="image" height="200" class="rounded-lg" />
          </div>
          <div v-else class="chart-container" style="height: 200px; position: relative;">
            <svg viewBox="0 0 100 50" preserveAspectRatio="none" style="width: 100%; height: 100%;">
              <defs>
                <linearGradient id="waveGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stop-color="#2D9CDB" stop-opacity="0.2" />
                  <stop offset="100%" stop-color="#2D9CDB" stop-opacity="0" />
                </linearGradient>
              </defs>
              <path v-if="chartOrderLinePath"
                :d="chartOrderLinePath" fill="none" stroke="#2D9CDB"
                stroke-width="1" stroke-linecap="round" />
              <path v-if="chartOrderFillPath && chartOrderHasData"
                :d="chartOrderFillPath" fill="url(#waveGradient)" stroke="none" />
              <!-- fallback when no order at all -->
              <text v-if="!chartOrderHasData" x="50" y="28" text-anchor="middle"
                font-size="4" fill="#9CA3AF">ยังไม่มีข้อมูลออเดอร์</text>
            </svg>

            <template v-if="chartOrderPeak">
              <div class="chart-dot"
                :style="{ left: chartOrderPeak.left, top: chartOrderPeak.top, backgroundColor: '#2D9CDB', borderColor: 'white' }">
              </div>
              <v-card class="pa-3 d-flex flex-column align-center rounded-lg" elevation="1"
                :style="{ position: 'absolute', top: chartOrderPeak.top, left: chartOrderPeak.left, transform: 'translate(-50%, -120%)' }">
                <span class="font-weight-bold caption mb-1">{{ chartOrderPeak.count }} ออเดอร์</span>
                <span class="text-caption grey--text" style="font-size: 0.65rem !important;">{{ chartOrderPeak.date }}</span>
              </v-card>
            </template>
          </div>

          <div class="d-flex justify-space-between px-2 chart-labels mt-4">
            <span v-for="(lbl, i) in chartOrderLabels" :key="i">{{ lbl }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row 2 -->
    <v-row class="mt-4">
      <!-- Total Revenue (dual line) -->
      <v-col cols="12" md="8">
        <v-card class="dashboard-card pa-6 h-100" elevation="0">
          <div class="d-flex justify-space-between align-center mb-8">
            <h3 class="card-title">รายได้รวมรายเดือน</h3>
            <div class="d-flex align-center">
              <div class="d-flex align-center mr-4">
                <v-icon x-small color="#2D9CDB" class="mr-2">mdi-circle</v-icon>
                <span class="text-caption font-weight-bold">{{ currentYear }}</span>
              </div>
              <div class="d-flex align-center">
                <v-icon x-small color="#FF5B5B" class="mr-2">mdi-circle</v-icon>
                <span class="text-caption font-weight-bold">{{ prevYear }}</span>
              </div>
            </div>
          </div>

          <div v-if="loadingStats">
            <v-skeleton-loader type="image" height="240" class="rounded-lg" />
          </div>
          <div v-else class="chart-area-wrapper-2 chart-scroll-mobile" style="height: 240px; position:relative;">
            <div class="d-flex flex-column justify-space-between h-100 text-caption grey--text text-right pr-2"
              style="position: absolute; left:0; top:0; bottom:20px; width: 40px; font-weight: 500;">
              <span v-for="lbl in revenueYLabels" :key="lbl">{{ lbl }}</span>
            </div>

            <svg viewBox="0 0 800 230" class="pl-10 pb-4" preserveAspectRatio="none"
              style="width: 100%; height: 100%;">
              <line x1="0" y1="47" x2="800" y2="47" stroke="#f3f4f6" stroke-width="1" />
              <line x1="0" y1="104" x2="800" y2="104" stroke="#f3f4f6" stroke-width="1" />
              <line x1="0" y1="161" x2="800" y2="161" stroke="#f3f4f6" stroke-width="1" />
              <line x1="0" y1="210" x2="800" y2="210" stroke="#f3f4f6" stroke-width="1" />

              <path v-if="revenueCurrentPath"
                :d="revenueCurrentPath" fill="none" stroke="#2D9CDB" stroke-width="3" />
              <path v-if="revenuePrevPath"
                :d="revenuePrevPath" fill="none" stroke="#FF5B5B" stroke-width="3" />
            </svg>

            <!-- Revenue summary labels -->
            <div v-if="revenueCurrentMax > 0"
              class="py-1 px-3 rounded-lg white elevation-2 blue--text text--darken-2 font-weight-bold caption"
              style="position:absolute; top: 8%; right: 4%; border: 1px solid #E3F2FD;">
              {{ formatCurrency(revenueCurrentMax) }}
            </div>
            <div v-if="revenuePrevMax > 0"
              class="py-1 px-3 rounded-lg white elevation-2 red--text text--lighten-1 font-weight-bold caption"
              style="position:absolute; top: 20%; right: 4%; border: 1px solid #FFEBEE;">
              {{ formatCurrency(revenuePrevMax) }}
            </div>
          </div>

          <div class="d-flex justify-space-between mt-2 pl-10 chart-labels">
            <span v-for="lbl in revenueMonthLabels" :key="lbl">{{ lbl }}</span>
          </div>
        </v-card>
      </v-col>

      <!-- Customer Map (orders by day of week) -->
      <v-col cols="12" md="4">
        <v-card class="dashboard-card pa-6 h-100" elevation="0">
          <div class="d-flex justify-space-between align-center mb-8">
            <h3 class="card-title">การกระจายตามวัน</h3>
            <v-chip small outlined color="grey">7 วันล่าสุด</v-chip>
          </div>

          <div v-if="loadingStats" class="d-flex align-end justify-space-between" style="height: 220px;">
            <v-skeleton-loader v-for="i in 7" :key="i" type="text" width="14" class="grow mx-1" height="100" />
          </div>
          <div v-else class="d-flex align-end justify-space-between" style="height: 220px;">
            <div v-for="bar in dayOfWeekBars" :key="bar.label" class="bar-group text-center grow">
              <div class="bar-bg rounded-t-lg mx-auto mb-3"
                :style="{ height: bar.height + 'px', width: '14px', background: bar.color }">
              </div>
              <span class="text-caption grey--text font-weight-medium">{{ bar.label }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top Products -->
    <div class="mt-8">
      <div class="d-flex align-center justify-space-between mb-4">
        <div>
          <h2 class="text-h6 font-weight-bold" style="color:#111827">สินค้าขายดี</h2>
          <p class="text-caption grey--text mt-1">5 อันดับสินค้าที่มียอดขายสูงสุด</p>
        </div>
      </div>

      <v-card class="dashboard-card" elevation="0">
        <v-skeleton-loader v-if="loadingStats" type="table-row@5" />
        <v-simple-table v-else>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">#</th>
                <th class="text-left">สินค้า</th>
                <th class="text-right">จำนวนที่ขาย</th>
                <th class="text-right">รายได้</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!topProducts.length">
                <td colspan="4" class="text-center grey--text py-6">ยังไม่มีข้อมูลสินค้าขายดี</td>
              </tr>
              <tr v-for="(product, idx) in topProducts" :key="product._id || idx">
                <td class="text-caption font-weight-bold grey--text">{{ idx + 1 }}</td>
                <td>
                  <div class="font-weight-medium text-body-2">{{ product.productName || '—' }}</div>
                </td>
                <td class="text-right">
                  <v-chip x-small color="blue lighten-5" text-color="#2D9CDB" class="font-weight-bold">
                    {{ product.quantitySold || 0 }} ชิ้น
                  </v-chip>
                </td>
                <td class="text-right font-weight-bold" style="color:#00C48C">
                  {{ formatCurrency(product.revenue || 0) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </div>

    <!-- Customer Review -->
    <div class="mt-8">
      <div class="d-flex align-center justify-space-between mb-4">
        <div>
          <h2 class="text-h6 font-weight-bold" style="color:#111827">รีวิวร้านค้า</h2>
          <div class="d-flex align-center mt-1" v-if="sellerReviewCount > 0">
            <v-rating :value="sellerAvg" color="amber" dense half-increments readonly size="18"></v-rating>
            <span class="font-weight-bold ml-2 text-body-1">{{ sellerAvg.toFixed(1) }}</span>
            <span class="text-caption grey--text ml-1">({{ sellerReviewCount }} รีวิว)</span>
          </div>
          <div v-else class="text-caption grey--text mt-1">ยังไม่มีรีวิว</div>
        </div>
      </div>

      <v-row v-if="sellerReviews.length > 0">
        <v-col cols="12" md="4" v-for="rv in sellerReviews.slice(0, 3)" :key="rv.id">
          <v-card class="dashboard-card pa-6" elevation="0">
            <div class="d-flex align-start mb-3">
              <v-avatar size="40" class="mr-3" color="#E8F5E9">
                <v-icon color="#198C19">mdi-account</v-icon>
              </v-avatar>
              <div class="grow min-width-0">
                <div class="font-weight-bold text-body-2">{{ rv.reviewer?.username || rv.reviewer?.firstName || 'ผู้ใช้' }}</div>
                <div class="text-caption grey--text">{{ formatReviewDate(rv.createdAt) }}</div>
              </div>
            </div>
            <div class="d-flex align-center mb-2">
              <v-rating :value="rv.rating" color="amber" dense readonly size="16"></v-rating>
              <span class="font-weight-bold ml-1 text-body-2">{{ rv.rating }}</span>
            </div>
            <p class="text-body-2 grey--text text--darken-1 mb-0" style="line-height: 1.6;">
              {{ rv.comment || '—' }}
            </p>
          </v-card>
        </v-col>
      </v-row>

      <v-card v-else-if="!loadingReviews" class="dashboard-card pa-6 text-center" elevation="0">
        <v-icon size="48" color="grey lighten-2">mdi-star-outline</v-icon>
        <p class="text-body-2 grey--text mt-2">ยังไม่มีรีวิวจากลูกค้า</p>
      </v-card>
    </div>

  </div>
</template>

<script>
import MetricCard from "@/components/SellerDashboard/Card.vue";
import { getSellerProfileApi } from "@/services/api/auth";
import { getSellerReviewsApi } from "@/services/api/review";
import {
  getSellerKpiCardsApi,
  getSellerOrderStatusSummaryApi,
  getSellerSalesByPeriodApi,
  getSellerTopProductsApi,
} from "@/services/api/sellerServices";
import {
  getNotificationsApi,
  markAsReadApi,
  markAllAsReadApi,
} from "@/services/api/notification";
import { getConversationsApi } from "@/services/api/chat";

export default {
  name: "SellerDashboard",
  components: { MetricCard },
  data() {
    return {
      sellerProfile: null,
      avatarError: false,
      sellerReviews: [],
      sellerAvg: 0,
      sellerReviewCount: 0,
      loadingReviews: false,

      kpiData: null,
      orderStatusData: null,
      weeklyData: [],
      monthlyDataCurrent: [],
      monthlyDataPrev: [],
      topProducts: [],
      loadingStats: false,

      selectedPeriod: "last7d",
      periodOptions: [
        { text: "วันนี้", value: "today" },
        { text: "7 วันล่าสุด", value: "last7d" },
        { text: "เดือนนี้", value: "thisMonth" },
        { text: "ทั้งหมด", value: "lifetime" },
      ],

      // Notifications
      notifMenu: false,
      notifications: [],
      notifLoading: false,

      // Chat
      chatMenu: false,
      chatConversations: [],
      chatLoading: false,
    };
  },
  computed: {
    unreadCount()     { return this.$store.getters.notifBadge; },
    chatUnreadCount() { return this.$store.getters.chatBadge; },

    sellerDisplayName() {
      if (!this.sellerProfile) return "";
      return this.sellerProfile.shopName || this.sellerProfile.username || "";
    },
    sellerAvatar() {
      if (this.avatarError) return null;
      const img = this.sellerProfile?.profileImage || this.sellerProfile?.storeImage || null;
      if (!img) return null;
      return img.startsWith("http") ? img : `${this.$domain_api}${img}`;
    },

    // --- Period ---
    periodLabel() {
      const map = { today: "วันนี้", last7d: "7 วันล่าสุด", thisMonth: "เดือนนี้", lifetime: "ทั้งหมด" };
      return map[this.selectedPeriod] || "";
    },
    periodDisplayDate() {
      const fmt = (d) => d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" });
      const today = new Date();
      if (this.selectedPeriod === "today") return fmt(today);
      if (this.selectedPeriod === "last7d") {
        const start = new Date(today);
        start.setDate(today.getDate() - 6);
        return `${fmt(start)} – ${fmt(today)}`;
      }
      if (this.selectedPeriod === "thisMonth") {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return `${fmt(start)} – ${fmt(today)}`;
      }
      return "ทั้งหมด";
    },

    // --- KPI cards ---
    currentKpi() {
      const empty = { totalSales: 0, totalOrders: 0, AOV: 0 };
      if (!this.kpiData) return empty;
      return this.kpiData[this.selectedPeriod] || this.kpiData.lifetime || empty;
    },
    cancelledCount() {
      if (!this.orderStatusData?.statusSummary) return 0;
      const s = this.orderStatusData.statusSummary;
      return (s.cancelled || 0) + (s.refunded || 0);
    },
    metrics() {
      const kpi = this.currentKpi;
      return [
        {
          title: "ออเดอร์ทั้งหมด",
          value: kpi.totalOrders,
          subtitle: this.periodLabel,
          icon: "mdi-file-document-outline",
          badgeIcon: "mdi-arrow-up",
          trendUp: true,
        },
        {
          title: "จัดส่งสำเร็จ",
          value: this.kpiData?.deliveredOrders ?? 0,
          subtitle: "ตลอดทั้งหมด",
          icon: "mdi-package-variant-closed",
          badgeIcon: "mdi-check",
          trendUp: true,
        },
        {
          title: "ยกเลิก / คืนเงิน",
          value: this.cancelledCount,
          subtitle: "ตลอดทั้งหมด",
          icon: "mdi-file-remove-outline",
          badgeIcon: "mdi-close",
          trendUp: false,
        },
        {
          title: "รายได้รวม",
          value: this.formatCurrency(kpi.totalSales),
          subtitle: this.periodLabel,
          icon: "mdi-shopping-outline",
          badgeIcon: "mdi-currency-usd",
          trendUp: (kpi.totalSales || 0) > 0,
        },
      ];
    },

    pieSize() {
      return this.$vuetify.breakpoint.xs ? 90 : 130;
    },
    pieStroke() {
      return this.$vuetify.breakpoint.xs ? 12 : 18;
    },
    pieFontClass() {
      return this.$vuetify.breakpoint.xs ? 'text-body-1' : 'text-h5';
    },

    // --- Pie Chart ---
    orderPieData() {
      const empty = { deliveredPct: 0, inProgressPct: 0, cancelledPct: 0, delivered: 0, inProgress: 0, cancelled: 0 };
      if (!this.orderStatusData?.statusSummary) return empty;
      const total = this.orderStatusData.totalOrders || 0;
      if (!total) return empty;
      const s = this.orderStatusData.statusSummary;
      const delivered = s.delivered || 0;
      const inProgress = (s.created || 0) + (s.request_paid || 0) + (s.paid || 0) +
        (s.packed || 0) + (s.shipped || 0) + (s.out_for_delivery || 0);
      const cancelled = (s.cancelled || 0) + (s.refunded || 0);
      return {
        delivered,
        inProgress,
        cancelled,
        deliveredPct: Math.round((delivered / total) * 100),
        inProgressPct: Math.round((inProgress / total) * 100),
        cancelledPct: Math.round((cancelled / total) * 100),
      };
    },

    // --- Chart Order (wave) ---
    chartWeeklyValues() {
      return this.weeklyData.map((d) => d.orderCount || 0);
    },
    chartOrderHasData() {
      return this.chartWeeklyValues.some((v) => v > 0);
    },
    chartOrderLinePath() {
      return this.buildSmoothLine(this.chartWeeklyValues, 100, 50, 4, 6);
    },
    chartOrderFillPath() {
      return this.buildFillArea(this.chartWeeklyValues, 100, 50, 4, 6);
    },
    chartOrderLabels() {
      if (!this.weeklyData.length) return ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
      return this.weeklyData.map((d) => {
        const date = new Date(d.period + "T00:00:00");
        return date.toLocaleDateString("th-TH", { weekday: "short" });
      });
    },
    chartOrderPeak() {
      const vals = this.chartWeeklyValues;
      if (!vals.length || !vals.some((v) => v > 0)) return null;
      const maxIdx = vals.reduce((best, v, i) => (v > vals[best] ? i : best), 0);
      const n = vals.length;
      const maxV = Math.max(...vals);
      const minV = Math.min(...vals);
      const range = maxV - minV || 1;
      const vbW = 100, vbH = 50, padTop = 4, padBottom = 6;
      const h = vbH - padTop - padBottom;
      const xSvg = n > 1 ? (maxIdx / (n - 1)) * vbW : vbW / 2;
      const ySvg = padTop + h - ((vals[maxIdx] - minV) / range) * h;
      const dateStr = this.weeklyData[maxIdx]?.period
        ? new Date(this.weeklyData[maxIdx].period + "T00:00:00").toLocaleDateString("th-TH", {
            day: "numeric", month: "short", year: "numeric",
          })
        : "";
      return {
        left: xSvg.toFixed(1) + "%",
        top: ((ySvg / vbH) * 100).toFixed(1) + "%",
        count: vals[maxIdx],
        date: dateStr,
      };
    },

    // --- Revenue Chart ---
    currentYear() {
      return new Date().getFullYear();
    },
    prevYear() {
      return new Date().getFullYear() - 1;
    },
    revenueCurrentValues() {
      return this.normalizeMonthly(this.monthlyDataCurrent, this.currentYear);
    },
    revenuePrevValues() {
      return this.normalizeMonthly(this.monthlyDataPrev, this.prevYear);
    },
    revenueMaxVal() {
      return Math.max(...this.revenueCurrentValues, ...this.revenuePrevValues, 0);
    },
    revenueCurrentMax() {
      return Math.max(...this.revenueCurrentValues, 0);
    },
    revenuePrevMax() {
      return Math.max(...this.revenuePrevValues, 0);
    },
    revenueCurrentPath() {
      if (!this.revenueCurrentValues.some((v) => v > 0)) return "";
      return this.buildSmoothLineWithMax(this.revenueCurrentValues, this.revenueMaxVal, 800, 220, 10, 20);
    },
    revenuePrevPath() {
      if (!this.revenuePrevValues.some((v) => v > 0)) return "";
      return this.buildSmoothLineWithMax(this.revenuePrevValues, this.revenueMaxVal, 800, 220, 10, 20);
    },
    revenueYLabels() {
      const maxVal = this.revenueMaxVal;
      if (!maxVal) return ["0", "0", "0", "0", "0"];
      const step = maxVal / 4;
      return [
        this.formatCurrencyShort(maxVal),
        this.formatCurrencyShort(step * 3),
        this.formatCurrencyShort(step * 2),
        this.formatCurrencyShort(step),
        "0",
      ];
    },
    revenueMonthLabels() {
      return ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
    },

    // --- Customer Map (day of week) ---
    dayOfWeekBars() {
      const dayLabels = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
      const colors = ["#FF5B5B", "#FFB020", "#FF5B5B", "#FFB020", "#FF5B5B", "#FFB020", "#FF5B5B"];
      const totals = Array(7).fill(0);
      for (const d of this.weeklyData) {
        const dt = new Date(d.period + "T00:00:00");
        totals[dt.getDay()] += d.orderCount || 0;
      }
      const maxVal = Math.max(...totals, 1);
      return totals.map((v, i) => ({
        label: dayLabels[i],
        count: v,
        height: Math.max(Math.round((v / maxVal) * 180), 4),
        color: colors[i],
      }));
    },
  },
  watch: {
    notifMenu(val) {
      if (val) this.fetchNotifications();
    },
    chatMenu(val) {
      if (val) this.fetchChatConversations();
    },
  },
  async created() {
    await Promise.all([this.loadProfile(), this.loadStats()]);
  },
  methods: {
    async loadProfile() {
      try {
        const data = await getSellerProfileApi();
        this.sellerProfile = data.seller || data;
        await this.loadSellerReviews();
      } catch (e) {
        console.error("[SellerDashboard] load profile failed:", e);
      }
    },
    async loadStats() {
      this.loadingStats = true;
      try {
        const now = new Date();
        const currYear = now.getFullYear();
        const prevYear = currYear - 1;
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 6);
        sevenDaysAgo.setHours(0, 0, 0, 0);
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);

        const [kpiRes, statusRes, weeklyRes, monthlyCurrentRes, monthlyPrevRes, topProductsRes] =
          await Promise.all([
            getSellerKpiCardsApi(),
            getSellerOrderStatusSummaryApi(),
            getSellerSalesByPeriodApi({
              groupBy: "day",
              startDate: sevenDaysAgo.toISOString().split("T")[0],
              endDate: tomorrow.toISOString().split("T")[0],
            }),
            getSellerSalesByPeriodApi({
              groupBy: "month",
              startDate: `${currYear}-01-01`,
              endDate: `${currYear}-12-31`,
            }),
            getSellerSalesByPeriodApi({
              groupBy: "month",
              startDate: `${prevYear}-01-01`,
              endDate: `${prevYear}-12-31`,
            }),
            getSellerTopProductsApi({ limit: 5 }),
          ]);

        this.kpiData = kpiRes;
        this.orderStatusData = statusRes;

        // Fill all 7 days — days without orders appear as 0 so the chart always has 7 points
        const rawWeekly = weeklyRes.data || [];
        const filled = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const found = rawWeekly.find((x) => x.period === key);
          filled.push(found || { period: key, orderCount: 0, totalSales: 0, AOV: 0 });
        }
        this.weeklyData = filled;

        this.monthlyDataCurrent = monthlyCurrentRes.data || [];
        this.monthlyDataPrev = monthlyPrevRes.data || [];
        this.topProducts = topProductsRes.data || [];
      } catch (e) {
        console.error("[SellerDashboard] load stats failed:", e);
      } finally {
        this.loadingStats = false;
      }
    },
    async loadSellerReviews() {
      if (!this.sellerProfile?.id) return;
      this.loadingReviews = true;
      try {
        const res = await getSellerReviewsApi(this.sellerProfile.id, { take: 3 });
        this.sellerReviews = res.data || [];
        this.sellerAvg = res.meta?.avg || 0;
        this.sellerReviewCount = res.meta?.count || 0;
      } catch (e) {
        console.error("[SellerDashboard] load reviews failed:", e);
      } finally {
        this.loadingReviews = false;
      }
    },

    selectPeriod(period) {
      this.selectedPeriod = period;
    },

    // ── Notification ──────────────────────────────────────────────────────────
    async fetchNotifications() {
      this.notifLoading = true;
      try {
        const res = await getNotificationsApi(1);
        this.notifications = res.data || [];
        this.$store.commit("SET_BADGE_COUNTS", {
          notif: this.notifications.filter((n) => !n.isRead).length,
        });
      } catch { /* silent */ } finally {
        this.notifLoading = false;
      }
    },
    async handleNotifClick(n) {
      if (!n.isRead) {
        try {
          await markAsReadApi(n.id);
          n.isRead = true;
          this.$store.commit("SET_BADGE_COUNTS", {
            notif: Math.max(0, this.unreadCount - 1),
          });
        } catch { /* silent */ }
      }
      this.notifMenu = false;
      if (n.refId) {
        this.$router.push({ name: "seller_order_detail", params: { id: n.refId } });
      }
    },
    async markAllRead() {
      try {
        await markAllAsReadApi();
        this.notifications.forEach((n) => { n.isRead = true; });
        this.$store.commit("SET_BADGE_COUNTS", { notif: 0 });
      } catch { /* silent */ }
    },
    // ── Chat ──────────────────────────────────────────────────────────────────
    async fetchChatConversations() {
      this.chatLoading = true;
      try {
        const data = await getConversationsApi("seller");
        this.chatConversations = (data || [])
          .sort((a, b) => (b.unreadCount - a.unreadCount) || (new Date(b.updatedAt) - new Date(a.updatedAt)))
          .slice(0, 10);
        this.$store.commit("SET_BADGE_COUNTS", {
          chat: this.chatConversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
        });
      } catch { /* silent */ } finally {
        this.chatLoading = false;
      }
    },
    goToChat() {
      this.chatMenu = false;
      this.$router.push({ name: "seller_chat" });
    },
    goToChatConversation(conversationId) {
      this.chatMenu = false;
      this.$router.push({ name: "seller_chat", query: { conversation: conversationId } });
    },
    resolveChatAvatar(u) {
      const img = u && u.profileImage;
      if (!img) return "";
      return img.startsWith("http") ? img : `${this.$domain_api}${img}`;
    },

    notifIcon(type) {
      const map = {
        new_order:    "mdi-package-variant",
        order_status: "mdi-clipboard-check-outline",
        low_stock:    "mdi-alert-outline",
        payment:      "mdi-cash-check",
      };
      return map[type] || "mdi-bell-outline";
    },
    notifColor(type) {
      const map = {
        new_order:    "#2D9CDB",
        order_status: "#00C48C",
        low_stock:    "#FFB020",
        payment:      "#F4811F",
      };
      return map[type] || "#9CA3AF";
    },
    timeAgo(dateStr) {
      if (!dateStr) return "";
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "เมื่อกี้";
      if (mins < 60) return `${mins} นาทีที่แล้ว`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs} ชั่วโมงที่แล้ว`;
      const days = Math.floor(hrs / 24);
      return `${days} วันที่แล้ว`;
    },

    normalizeMonthly(data, year) {
      const result = Array(12).fill(0);
      for (const d of data) {
        const parts = (d.period || "").split("-");
        if (parseInt(parts[0]) === year) {
          const month = parseInt(parts[1]);
          if (month >= 1 && month <= 12) result[month - 1] = d.totalSales || 0;
        }
      }
      return result;
    },

    buildSmoothLine(values, vbW, vbH, padTop, padBottom) {
      if (!values || values.length === 0) return "";
      const n = values.length;
      const h = vbH - padTop - padBottom;
      const midY = (padTop + vbH - padBottom) / 2;

      // Single point or all-zero → flat line in the middle
      if (n === 1 || values.every((v) => v === 0)) {
        return `M0,${midY.toFixed(2)} L${vbW},${midY.toFixed(2)}`;
      }

      const maxV = Math.max(...values);
      const minV = Math.min(...values);
      const range = maxV - minV || 1;
      const pts = values.map((v, i) => ({
        x: (i / (n - 1)) * vbW,
        y: padTop + h - ((v - minV) / range) * h,
      }));
      let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const cp1x = pts[i].x + (pts[i + 1].x - pts[i].x) * 0.4;
        const cp2x = pts[i].x + (pts[i + 1].x - pts[i].x) * 0.6;
        d += ` C${cp1x.toFixed(2)},${pts[i].y.toFixed(2)} ${cp2x.toFixed(2)},${pts[i + 1].y.toFixed(2)} ${pts[i + 1].x.toFixed(2)},${pts[i + 1].y.toFixed(2)}`;
      }
      return d;
    },

    buildFillArea(values, vbW, vbH, padTop, padBottom) {
      const line = this.buildSmoothLine(values, vbW, vbH, padTop, padBottom);
      if (!line) return "";
      return `${line} L${vbW},${vbH} L0,${vbH} Z`;
    },

    buildSmoothLineWithMax(values, maxVal, vbW, vbH, padTop, padBottom) {
      if (!values || !values.length) return "";
      const n = values.length;
      const range = maxVal || 1;
      const h = vbH - padTop - padBottom;
      const pts = values.map((v, i) => ({
        x: n > 1 ? (i / (n - 1)) * vbW : vbW / 2,
        y: padTop + h - (v / range) * h,
      }));
      if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
      let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const cp1x = pts[i].x + (pts[i + 1].x - pts[i].x) * 0.4;
        const cp2x = pts[i].x + (pts[i + 1].x - pts[i].x) * 0.6;
        d += ` C${cp1x.toFixed(2)},${pts[i].y.toFixed(2)} ${cp2x.toFixed(2)},${pts[i + 1].y.toFixed(2)} ${pts[i + 1].x.toFixed(2)},${pts[i + 1].y.toFixed(2)}`;
      }
      return d;
    },

    formatCurrency(val) {
      if (!val && val !== 0) return "฿0";
      if (val >= 1000000) return `฿${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `฿${(val / 1000).toFixed(1)}K`;
      return `฿${Math.round(val).toLocaleString("th-TH")}`;
    },

    formatCurrencyShort(val) {
      if (!val && val !== 0) return "0";
      if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `${Math.round(val / 1000)}K`;
      return Math.round(val).toString();
    },

    formatReviewDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
</script>

<style scoped>
.seller-dashboard {
  background-color: #F8F9FB;
  min-height: 100vh;
  font-family: 'Poppins', sans-serif !important;
}

.dashboard-title {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.5px;
}

.dashboard-subtitle {
  font-size: 14px;
  color: #9CA3AF;
}

.modern-search ::v-deep .v-input__slot {
  padding-left: 20px !important;
  padding-right: 14px !important;
  box-shadow: 0px 5px 20px 0px rgba(0, 0, 0, 0.03) !important;
}

.modern-icon-btn {
  border-radius: 12px;
  width: 48px;
  height: 48px;
}

.modern-icon-btn:hover {
  transform: translateY(-2px);
}

.filter-card {
  border-radius: 14px;
  box-shadow: 0px 5px 20px 0px rgba(0, 0, 0, 0.03) !important;
  border: 1px solid #f0f0f0;
}

.filter-icon-box {
  width: 32px;
  height: 32px;
  background-color: #E3F2FD;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-card {
  border-radius: 20px;
  box-shadow: 0px 5px 20px 0px rgba(0, 0, 0, 0.03) !important;
  height: 100%;
  transition: box-shadow 0.2s;
}

.dashboard-card:hover {
  box-shadow: 0px 10px 30px 0px rgba(0, 0, 0, 0.06) !important;
}

.card-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.chart-labels {
  color: #9CA3AF;
  font-size: 12px;
  font-weight: 500;
}

.gap-3 {
  gap: 12px;
}

.chart-circle-wrapper {
  position: relative;
  width: 130px;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-dot {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translate(-50%, -50%);
}

.circle-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #F8F9FB;
  z-index: 0;
}

.custom-progress {
  z-index: 1;
}

@media (max-width: 599px) {
  .dashboard-title { font-size: 20px; }
  .card-title { font-size: 15px; }
  .chart-scroll-mobile { overflow-x: auto; min-width: 0; }
  .gap-3 { gap: 8px; }
  .modern-icon-btn { width: 40px !important; height: 40px !important; }
}

.notif-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.notif-list {
  max-height: 380px;
  overflow-y: auto;
}
.notif-item {
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background 0.15s;
}
.notif-item:hover {
  background: #F8F9FB !important;
}
.notif-unread {
  background: #EFF6FF !important;
}
.notif-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2D9CDB;
}
</style>
